// <copyright file="DleRecordTypeDetail.tsx">
// © 2024 Florida Department of Transportation
//
// The copyright to the computer program(s) and source code herein is
// the property of the Florida Department of Transportation.
// The program(s) and source code may be used and/or copied only with the
// written permission of the Florida Department of Transportation or
// in accordance with the terms and conditions stipulated in the
// agreement/contract under which the program(s) and source code have
// been supplied.
// </copyright>

/* eslint-disable react/no-unescaped-entities */

import { Column } from "primereact/column";
import { DataTable } from "primereact/datatable";
import { Toast } from "primereact/toast";
import React from "react";
import styles from "src/components/DataLakeExplorer/DleRecordTypeDetail.module.css";
import { sortDleTableNames } from "src/utils/SharedUtils";
import type { FC, useState as _useState } from "react";
import type { RecordType } from "src/components/DataLakeExplorer/DataLakeExplorer";
import type {
    SchemaVersion,
    SchemaVersionDetail,
} from "src/components/DataLakeExplorer/DleRecordType";

// Properties to initialize this component
interface DleRecordTypeDetailProps {
    useState?: typeof _useState; // optional hook override (for tests)
    recordType: string;
    allRecordTypes: RecordType[];
    allSchemVersions: SchemaVersion[];
    schemaVersionDetail: SchemaVersionDetail | undefined;
}

export interface DleTableInfo {
    tableName: string;
    description: string;
}

// Info/error message popup
const toast = React.createRef<Toast>();

/**
 * Determines whether to display the content view for this component
 * @param props Component properties
 * @returns True if view should display, else false
 */
function shouldDisplayView(props: DleRecordTypeDetailProps) {
    return props.schemaVersionDetail !== undefined;
}

/**
 * DLE record type detail compomnent
 * @param props Component props
 * @returns The component
 */
const DleRecordTypeDetail: FC<DleRecordTypeDetailProps> = (
    props: DleRecordTypeDetailProps
) => {
    console.debug(
        `record details mounted ${props.recordType} ${props.schemaVersionDetail?.MetadataVersion}`
    );
    const formatDiv = (
        <div>
            Curated records are stored in S3 as bulk (multi-record) Parquet
            files written using dataset-specific Avro schemas. All records
            include common metadata to facilitate filtering data by sub-type,
            time, location, and source. Records can be queried, viewed, and
            downloaded using the AWS Athena Query Editor using a HiveQL syntax
            and leveraging the data catalog partitions and indexes.
            <br />
            <br />
            See "Schema Details" and "Sample Queries" for more information.
            <br />
            <br />
            References:
            https://docs.aws.amazon.com/athena/latest/ug/ddl-sql-reference.html
        </div>
    );

    // Load data tables for schema
    const dataTables: DleTableInfo[] = [];
    props.allSchemVersions
        .filter(
            (sv) =>
                sv.MetadataVersion == props.schemaVersionDetail?.MetadataVersion
        )
        .forEach((sv) => {
            const dt: DleTableInfo = {
                tableName: sv.TableName,
                description:
                    props.allRecordTypes.find(
                        (rt) =>
                            sv.TableName.split("_")[0] === rt.TableNamePrefix &&
                            rt.CuratedSchema == sv.SchemaName
                    )?.TableDescription || "n/a",
            };
            dataTables.push(dt);
        });
    const sortedDataTables = [...dataTables].sort((aa, bb) => sortDleTableNames(aa, bb));
    const dataTableColumns = [
        { field: "tableName", header: "Table Name" },
        { field: "description", header: "Description" },
    ];

    // Load schema version details
    const startDate: string | undefined =
        props.schemaVersionDetail?.StartDate?.substring(0, 16);
    const endDate: string | undefined =
        props.schemaVersionDetail?.EndDate?.substring(0, 16);
    let isOngoing = props.schemaVersionDetail?.IsOngoing;
    isOngoing = false; // Always disable isOngoing for now

    const detailsDataList = () => (
        <dl className="verticalTable">
            <dt>Name</dt>
            <dd>{props.schemaVersionDetail?.SchemaName}</dd>
            <dt>Version</dt>
            <dd>{`${props.schemaVersionDetail?.MetadataVersion} ${
                props.schemaVersionDetail?.IsLatest ? "(latest)" : ""
            }`}</dd>
            <dt>Description</dt>
            <dd>{props.schemaVersionDetail?.Description}</dd>
            <dt>First Partition</dt>
            <dd>{`${startDate ?? "unknown"} UTC`}</dd>
            <dt>Last Partition</dt>
            <dd>{`${endDate ?? "unknown"} UTC ${
                isOngoing ? " (ongoing)" : ""
            }`}</dd>
            <dt>Format</dt>
            <dd>{formatDiv}</dd>
            <dt>Data Tables</dt>
            <dd>
                <DataTable value={sortedDataTables} showGridlines>
                    {dataTableColumns.map((col) => (
                        <Column
                            key={col.field}
                            field={col.field}
                            header={col.header}
                        />
                    ))}
                </DataTable>
            </dd>
        </dl>
    );

    return (
        <div
            className={styles.DleRecordTypeDetail}
            data-testid="DleRecordTypeDetail"
        >
            <Toast ref={toast} />
            {(shouldDisplayView(props) && detailsDataList()) || (
                <div>Schema version detail content was not loaded.</div>
            )}
        </div>
    );
};

export default DleRecordTypeDetail;
